import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Swal from 'sweetalert2';
import { Button, Input, Select, Spinner, Alert } from '@components/ui';
import { PhotoIcon, PlusIcon, ShieldCheckIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useCompanySettings, useUpdateCompanySettings } from '../hooks/useCompany';
import { companyService } from '../services/companyService';
import { useAuthStore } from '@/features/auth/store/authStore';
import { canAccessRoute } from '@lib/utils/roleNavigation';
import { NewCompanyWizardModal } from '@features/companies/components/wizard/NewCompanyWizardModal';

const companySchema = z.object({
  ruc: z.string().min(1, 'El RUC es requerido').max(20, 'Máximo 20 caracteres'),
  dv: z.string().min(1, 'El DV es requerido').max(2, 'Máximo 2 caracteres'),
  fullName: z.string().min(2, 'Mínimo 2 caracteres').max(200, 'Máximo 200 caracteres'),
  name: z.string().min(2, 'Mínimo 2 caracteres').max(200, 'Máximo 200 caracteres'),
  typeTaxpayer: z.union([
    z.number().min(1, 'El tipo de contribuyente es requerido').max(2),
    z.literal(''),
  ]).refine((val) => val !== '', { message: 'El tipo de contribuyente es requerido' }),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  phone: z.string().min(1, 'El teléfono es requerido').max(16, 'Máximo 16 caracteres')
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true;
        if (val.length < 7 || val.length > 16) return false;
        if (!/^[\d\-]+$/.test(val)) return false;
        const digitsOnly = val.replace(/-/g, '');
        if (digitsOnly.length < 7 || digitsOnly.length > 15) return false;
        return true;
      },
      { message: 'Formato DGI: 999-9999, 9999-9999 o código-999-9999 (7-16 chars)' }
    ),
  address: z.string().min(1, 'La dirección es requerida').max(100, 'Máximo 100 caracteres'),
  country: z.string().min(1, 'El país es requerido'),
});

type CompanyFormData = z.infer<typeof companySchema>;

export function EditCompanyPage() {
  const user = useAuthStore((state) => state.user);
  // '/company/new' ya no es una ruta real — se reutiliza como clave de permiso
  // para mantener la misma regla de roles que el resto de rutas de Empresa.
  const canCreateCompany = canAccessRoute(user?.role, '/company/new');
  const [isNewCompanyModalOpen, setIsNewCompanyModalOpen] = useState(false);
  const { data: company, isLoading, error } = useCompanySettings();
  const updateMutation = useUpdateCompanySettings();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    mode: 'onChange',
    values: {
      ruc: company?.ruc || '',
      dv: company?.dv || '',
      fullName: company?.fullName || '',
      name: company?.name || '',
      typeTaxpayer: company?.typeTaxpayer ? Number(company.typeTaxpayer) : 0,
      email: company?.email || '',
      phone: company?.phone || '',
      address: company?.address || '',
      country: company?.country || '',
    },
  });

  /**
   * Completitud del perfil: porcentaje de campos del formulario con valor.
   * Se calcula sobre los valores reales del form (watch), no es un numero
   * decorativo — si el usuario completa un campo, sube.
   */
  const values = watch();
  const PROFILE_FIELDS: Array<keyof CompanyFormData> = [
    'typeTaxpayer', 'ruc', 'dv', 'fullName', 'name', 'email', 'phone', 'country', 'address',
  ];
  const filledCount = PROFILE_FIELDS.filter((f) => {
    const v = values[f];
    return v !== undefined && v !== null && String(v).trim() !== '' && v !== 0;
  }).length;
  // El logo cuenta como un campo mas del perfil.
  const hasLogo = !!(logoPreview || company?.profileImage);
  const completeness = Math.round(
    ((filledCount + (hasLogo ? 1 : 0)) / (PROFILE_FIELDS.length + 1)) * 100
  );

  /**
   * Secciones con cambios sin guardar. Sale de dirtyFields de react-hook-form,
   * asi que refleja ediciones reales y no un estado inventado.
   */
  const LEGAL_FIELDS = ['typeTaxpayer', 'ruc', 'dv', 'fullName', 'name'] as const;
  const CONTACT_FIELDS = ['email', 'phone', 'country', 'address'] as const;
  const dirtySections = [
    LEGAL_FIELDS.some((f) => dirtyFields[f]) ? 'Información Legal' : null,
    CONTACT_FIELDS.some((f) => dirtyFields[f]) ? 'Contacto' : null,
  ].filter(Boolean) as string[];
  const hasPendingChanges = isDirty || !!logoFile;

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 1MB = 1048576 bytes)
      const maxSize = 1 * 1024 * 1024; // 1MB
      if (file.size > maxSize) {
        await Swal.fire({
          icon: 'error',
          title: 'Archivo muy grande',
          text: 'La imagen no debe ser mayor a 1MB',
          confirmButtonColor: '#ef4444',
        });
        setLogoFile(null);
        setLogoPreview(null);
        e.target.value = ''; // Reset input
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        await Swal.fire({
          icon: 'error',
          title: 'Tipo de archivo inválido',
          text: 'El archivo debe ser una imagen',
          confirmButtonColor: '#ef4444',
        });
        setLogoFile(null);
        setLogoPreview(null);
        e.target.value = ''; // Reset input
        return;
      }

      setUploadError(null);
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: CompanyFormData) => {
    try {
      setUploadError(null);
      let profileImageUrl = company?.profileImage;

      // Upload logo if a new file was selected
      if (logoFile && company?.id) {
        setIsUploading(true);
        try {
          const uploadResult = await companyService.uploadFile(
            logoFile,
            company.id,
            'company'
          );
          profileImageUrl = uploadResult.url;
          console.log('✅ Logo uploaded, URL:', profileImageUrl);
        } catch (uploadErr: any) {
          console.error('Error uploading logo:', uploadErr);
          setIsUploading(false);

          await Swal.fire({
            icon: 'error',
            title: 'Error al subir imagen',
            text: uploadErr?.response?.data?.message || uploadErr?.message || 'Error al subir la imagen. Por favor, intenta de nuevo.',
            confirmButtonColor: '#ef4444',
          });

          return;
        } finally {
          setIsUploading(false);
        }
      }

      // Update company with new data and logo URL
      const updatePayload = {
        ...data,
        email: data.email, // Use company's email
        ...(profileImageUrl && { profileImage: profileImageUrl }),
      };

      console.log('📤 Updating company with payload:', updatePayload);

      await updateMutation.mutateAsync(updatePayload);

      await Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Información de la empresa actualizada exitosamente',
        confirmButtonColor: '#10b981',
      });

      setLogoFile(null);
      setLogoPreview(null);
    } catch (error: any) {
      console.error('Error updating company:', error);

      const errorMessage = error?.response?.data?.message || error?.message || 'Error al actualizar la información de la empresa';

      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonColor: '#ef4444',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" title="Error al cargar información" data-testid="edit-company-load-error-alert">
        Ocurrió un error al cargar la información de la empresa. Por favor, intenta de nuevo.
      </Alert>
    );
  }

  /** Rail izquierdo de cada seccion: numero, titulo, descripcion y nota. */
  const SectionRail = ({
    num,
    title,
    description,
    children,
  }: {
    num: string;
    title: string;
    description: string;
    children?: React.ReactNode;
  }) => (
    <div>
      <div className="font-mono text-[12px] font-bold tracking-[0.12em] text-secondary-400">
        {num} · SECCIÓN
      </div>
      <h2 className="mt-2 text-xl font-extrabold tracking-[-0.02em] text-ink-850 dark:text-white">
        {title}
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-secondary-500 dark:text-secondary-400">
        {description}
      </p>
      {children}
    </div>
  );

  const CARD =
    'rounded-2xl border border-[#ececec] bg-white shadow-[0_1px_2px_rgba(10,34,51,.03),0_12px_32px_-18px_rgba(10,34,51,.14)] dark:border-secondary-700 dark:bg-secondary-800';
  /** Grilla de seccion: rail a la izquierda, campos a la derecha. */
  const SPLIT = 'grid grid-cols-1 items-start gap-5 xl:grid-cols-[260px_minmax(0,1fr)] xl:gap-10';

  const CIRC = 2 * Math.PI * 32;

  return (
    <div className="font-display">
      {/* Cabecera */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          {/*
            El ultimo segmento del breadcrumb ES el titulo (h1) — sin eyebrow
            arriba (quedaba de mas junto al breadcrumb).
          */}
          <nav className="flex items-baseline gap-2">
            <span className="text-[14px] text-secondary-400">Inicio</span>
            <span className="text-[14px] text-secondary-300">/</span>
            <h1 className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">
              Editar Empresa
            </h1>
          </nav>
          <p className="mt-2 text-[15px] text-secondary-500 dark:text-secondary-400">
            Configura la información de tu empresa
          </p>
        </div>
        {canCreateCompany && (
          <button
            type="button"
            onClick={() => setIsNewCompanyModalOpen(true)}
            data-testid="edit-company-new-company-button"
            className="inline-flex items-center gap-2 rounded-[10px] border border-[#ececec] bg-white px-3.5 py-2.5 text-[14px] font-medium text-ink-850 transition-colors hover:border-secondary-300 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Nueva compañía
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit as any)}>
        {/* ============= MEMBRETE / IDENTIDAD ============= */}
        <div className={`${CARD} relative mt-7 overflow-hidden`}>
          <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-teal-500 via-[#3a7ac7] to-[#6b239e]" />

          <div className="grid grid-cols-1 items-center gap-8 p-7 sm:px-8 lg:grid-cols-[200px_minmax(0,1fr)] xl:grid-cols-[200px_minmax(0,1fr)_auto]">
            {/* Zona de logo */}
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="hidden"
                id="logo-upload"
                disabled={isUploading}
               data-testid="edit-company-logo-upload-input"/>
              <label
                htmlFor="logo-upload"
                className={`flex h-[200px] w-[200px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-[1.5px] border-dashed border-secondary-300 bg-secondary-50 transition-colors hover:border-secondary-400 hover:bg-white dark:border-secondary-600 dark:bg-secondary-900 ${
                  isUploading ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                {logoPreview || company?.profileImage ? (
                  <img
                    src={logoPreview || company?.profileImage || ''}
                    alt="Logo de la empresa"
                    className="h-full w-full object-contain p-3"
                  />
                ) : (
                  <>
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-[14px] border border-[#ececec] bg-white text-secondary-400 dark:border-secondary-700 dark:bg-secondary-800">
                      <PhotoIcon className="h-6 w-6" />
                    </span>
                    <span className="mt-3 font-mono text-[12px] tracking-[0.06em] text-secondary-400">
                      200 × 200
                    </span>
                  </>
                )}
              </label>
            </div>

            {/* Resumen de la compañía */}
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-secondary-400">
                <span className="h-1.5 w-1.5 rounded-full bg-teal-500 shadow-[0_0_8px_#12c4b2]" />
                Logo de la empresa
              </div>
              <div className="mt-2 text-2xl font-extrabold leading-tight tracking-[-0.025em] text-ink-850 dark:text-white">
                {company?.name || company?.fullName || 'Sin nombre'}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3.5 font-mono text-xs text-secondary-500">
                <span>
                  RUC <span className="font-semibold text-ink-850 dark:text-white">{company?.ruc || '—'}</span>{' '}
                  <span className="text-secondary-400">· DV {company?.dv || '—'}</span>
                </span>
                <span className="h-2.5 w-px bg-secondary-200 dark:bg-secondary-700" />
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0d9488]/10 px-2.5 py-0.5 font-display text-[11px] font-bold tracking-[0.12em] text-[#0d9488]">
                  <span className="h-[5px] w-[5px] rounded-full bg-[#0d9488]" />
                  ACTIVO
                </span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <label
                  htmlFor="logo-upload"
                  data-testid="edit-company-logo-upload-button"
                  className={`cta-sheen inline-flex flex-shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-[10px] bg-ink-850 px-4 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#142d40] ${
                    isUploading ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                >
                  <PhotoIcon className="h-3.5 w-3.5 flex-shrink-0" />
                  {isUploading ? 'Subiendo...' : 'Seleccionar imagen'}
                </label>
                {logoFile && !isUploading && (
                  <span className="min-w-0 truncate text-sm text-secondary-500 dark:text-secondary-400">
                    {logoFile.name}
                  </span>
                )}
              </div>
              {uploadError && (
                <p className="mt-2 text-sm text-danger-600 dark:text-danger-400" data-testid="edit-company-logo-upload-error-alert">{uploadError}</p>
              )}
              <p className="mt-2.5 text-[12px] text-secondary-400">
                Formatos aceptados: JPG, PNG · Tamaño máximo: 1 MB
              </p>
            </div>

            {/* Completitud del perfil — calculada sobre los campos con valor */}
            <div className="border-t border-dashed border-secondary-200 pt-5 text-center xl:border-l xl:border-t-0 xl:pl-6 xl:pt-0 dark:border-secondary-700">
              <svg viewBox="0 0 80 80" className="mx-auto h-[84px] w-[84px]">
                <circle cx="40" cy="40" r="32" fill="none" stroke="#eef2f6" strokeWidth="6" />
                <circle
                  cx="40"
                  cy="40"
                  r="32"
                  fill="none"
                  stroke="url(#complete-grad)"
                  strokeWidth="6"
                  strokeDasharray={CIRC}
                  strokeDashoffset={CIRC * (1 - completeness / 100)}
                  strokeLinecap="round"
                  transform="rotate(-90 40 40)"
                />
                <defs>
                  <linearGradient id="complete-grad" x1="0" x2="1">
                    <stop offset="0" stopColor="#12c4b2" />
                    <stop offset="1" stopColor="#6b239e" />
                  </linearGradient>
                </defs>
                <text
                  x="40"
                  y="42"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontWeight="800"
                  fontSize="18"
                  letterSpacing="-1"
                  className="fill-ink-850 dark:fill-white"
                >
                  {completeness}
                </text>
                <text x="40" y="55" textAnchor="middle" fontSize="7" fill="#94a3b8" letterSpacing="0.5" className="font-mono">
                  PERCENT
                </text>
              </svg>
              <div className="mt-1.5 text-[12px] font-bold uppercase leading-tight tracking-[0.14em] text-secondary-500">
                Perfil
                <br />
                completo
              </div>
            </div>
          </div>
        </div>

        {/* ============= 01 · INFORMACIÓN LEGAL ============= */}
        <div className={`${CARD} mt-5 p-7 sm:px-8`}>
          <div className={SPLIT}>
            <SectionRail
              num="01"
              title="Información Legal"
              description="Datos fiscales oficiales según registro DGI. Aparecen en cada documento electrónico emitido."
            >
              <div className="mt-4 rounded-[10px] border border-teal-500/[.16] bg-gradient-to-br from-teal-500/[.06] to-[#6b239e]/[.04] px-3.5 py-3">
                <div className="flex items-start gap-2.5">
                  <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-teal-500/[.24] bg-white text-[#0d9488] dark:bg-secondary-800">
                    <ShieldCheckIcon className="h-3 w-3" />
                  </span>
                  <div className="text-[12px] leading-relaxed text-secondary-500 dark:text-secondary-400">
                    Los cambios se sincronizan con DGI Panamá en la próxima emisión.
                  </div>
                </div>
              </div>
            </SectionRail>

            <div className="flex flex-col gap-4">
              <Select
                label="Tipo de Contribuyente"
                error={errors.typeTaxpayer?.message}
                options={[
                  { value: '', label: 'Seleccionar tipo de contribuyente...' },
                  { value: '1', label: 'Natural' },
                  { value: '2', label: 'Jurídico' },
                ]}
                {...register('typeTaxpayer', {
                  setValueAs: (v) => v === '' ? '' : Number(v)
                })}
               data-testid="edit-company-type-taxpayer-select"/>

              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-[2.4fr_1fr]">
                <Input
                  label="RUC"
                  placeholder="0000000-0-000000"
                  error={errors.ruc?.message}
                  maxLength={20}
                  className="font-mono"
                  {...register('ruc')}
                 data-testid="edit-company-ruc-input"/>
                <Input
                  label="DV"
                  placeholder="00"
                  error={errors.dv?.message}
                  maxLength={2}
                  className="text-center font-mono font-bold tracking-[0.08em]"
                  {...register('dv')}
                 data-testid="edit-company-dv-input"/>
              </div>

              <Input
                label="Razón Social"
                placeholder="Nombre legal de la empresa"
                error={errors.fullName?.message}
                maxLength={200}
                {...register('fullName')}
               data-testid="edit-company-full-name-input"/>

              <Input
                label="Nombre Comercial"
                placeholder="Nombre comercial o marca"
                error={errors.name?.message}
                maxLength={200}
                {...register('name')}
               data-testid="edit-company-name-input"/>
            </div>
          </div>
        </div>

        {/* ============= 02 · INFORMACIÓN DE CONTACTO ============= */}
        <div className={`${CARD} mt-5 p-7 sm:px-8`}>
          <div className={SPLIT}>
            <SectionRail
              num="02"
              title="Información de Contacto"
              description="Canales por los que tus clientes reciben las facturas y datos que aparecen en el pie del CAFE."
            >
              <div className="mt-4 flex items-center gap-2 font-mono text-[12px] tracking-[0.06em] text-secondary-400">
                <EnvelopeIcon className="h-3 w-3" />
                REMITENTE ACTUAL
              </div>
              <div className="mt-1.5 break-all font-mono text-[13px] font-semibold text-ink-850 dark:text-white">
                {company?.email || '—'}
              </div>
            </SectionRail>

            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                <Input
                  label="Email"
                  type="email"
                  placeholder="contacto@empresa.com"
                  disabled
                  error={errors.email?.message}
                  {...register('email')}
                 data-testid="edit-company-email-input"/>
                <Input
                  label="Teléfono"
                  placeholder="+507 0000-0000"
                  error={errors.phone?.message}
                  maxLength={16}
                  className="font-mono"
                  {...register('phone')}
                 data-testid="edit-company-phone-input"/>
              </div>

              <Select
                label="País"
                error={errors.country?.message}
                options={[
                  { value: 'Panamá', label: 'Panamá' },
                  { value: 'Costa Rica', label: 'Costa Rica' },
                  { value: 'Colombia', label: 'Colombia' },
                  { value: 'Otro', label: 'Otro' },
                ]}
                {...register('country')}
               data-testid="edit-company-country-select"/>

              <Input
                label="Dirección"
                placeholder="Dirección completa"
                error={errors.address?.message}
                maxLength={100}
                {...register('address')}
               data-testid="edit-company-address-input"/>
            </div>
          </div>
        </div>

        {/*
          Barra de guardado fija. El aviso sale de dirtyFields de
          react-hook-form, asi que nombra las secciones realmente editadas.
          "Descartar" hace reset() a los valores del servidor y limpia el logo
          pendiente — no toca nada mas.
        */}
        <div className="sticky bottom-0 z-[5] -mx-4 mt-5 flex flex-wrap items-center justify-between gap-4 border-t border-[#eeeeef] bg-white/[.86] px-4 py-3.5 backdrop-blur-xl sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 dark:border-secondary-700 dark:bg-secondary-900/[.86]">
          <div className="flex items-center gap-3">
            <span
              className={`h-2 w-2 rounded-full ${
                hasPendingChanges
                  ? 'bg-highlight-400 shadow-[0_0_8px_rgba(250,218,81,.6)]'
                  : 'bg-[#0d9488] shadow-[0_0_8px_rgba(13,148,136,.5)]'
              }`}
            />
            <span className="text-[13px] text-secondary-500 dark:text-secondary-400">
              {hasPendingChanges ? (
                <>
                  Cambios sin guardar
                  {dirtySections.length > 0 && (
                    <>
                      {' '}en{' '}
                      {dirtySections.map((sec, i) => (
                        <span key={sec}>
                          {i > 0 && ' y '}
                          <b className="font-bold text-ink-850 dark:text-white">{sec}</b>
                        </span>
                      ))}
                    </>
                  )}
                  {logoFile && dirtySections.length === 0 && (
                    <>
                      {' '}en <b className="font-bold text-ink-850 dark:text-white">Logo</b>
                    </>
                  )}
                  .
                </>
              ) : (
                'Sin cambios pendientes.'
              )}
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => {
                reset();
                setLogoFile(null);
                setLogoPreview(null);
                setUploadError(null);
              }}
              disabled={!hasPendingChanges}
              className="inline-flex items-center gap-2 rounded-[10px] border border-[#ececec] bg-white px-3.5 py-2.5 text-[14px] font-medium text-ink-850 transition-colors hover:border-secondary-300 disabled:cursor-not-allowed disabled:opacity-40 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
            >
              Descartar
            </button>
            <Button
              type="submit"
              data-testid="edit-company-submit-button"
              variant="primary"
              isLoading={updateMutation.isPending || isUploading}
              disabled={isUploading}
            >
              {isUploading ? 'Subiendo imagen...' : 'Guardar Cambios'}
            </Button>
          </div>
        </div>
      </form>

      {canCreateCompany && (
        <NewCompanyWizardModal
          isOpen={isNewCompanyModalOpen}
          onClose={() => setIsNewCompanyModalOpen(false)}
        />
      )}
    </div>
  );
}
